import React from 'react';
import { hot } from 'react-hot-loader';
import Link from 'redux-first-router-link';
import Select from '../Elements/Select/Select';
import { difference, intersect } from '../../utils/array';
import { removeLanguagePrefix } from '../../utils/locale';
import { urlify, translate, prefix } from '../../utils/string';
import s from './Navigation.scss';

const Navigation = ({ navigation, setup, menuPopupHeight, productCache = {}, openMenuHandler, expandMenuHandler, resetFilterHandler, changeLanguage, pagePaths, countries, onCountryChange }) => (
	<nav className={s.navigation}>
		<a className={[s.navigation_mobileMenu, ...(navigation.open ? [s.navigation_mobileMenu__open] : [])].join(' ')} href="#open-mobile-menu" title="Open mobile menu" onClick={openMenuHandler} aria-labelledby="Open mobile menu"><span /><span /><span /></a>
		<ul className={s.level1} style={{ maxHeight: `${menuPopupHeight}px` }}>
			{
				navigation.header.map((level1, i) => {
					const level1URI = translate(setup, level1, 'uri');
					const combinedURILevel1 = prefix(setup, level1URI);
					const level2SubCategoryLinks = [];
					const level2Links = [];
					const level1Styles = level1.styles || null;

					if (level1.subPages) {
						// Get column with most links
						const dropdownCSS = {};
						const maxTotalLinks = level1.subPages.reduce((acc, val) => (val.subPages ? [...acc, (val.subPages.length + 1)] : acc), []).sort((a, b) => b - a);
						let listHeight = maxTotalLinks.length > 0 && maxTotalLinks[0] > 7 ? maxTotalLinks[0] / 2 : maxTotalLinks[0];
						if (listHeight < maxTotalLinks.slice(-1)) listHeight = maxTotalLinks.slice(-1);
						if (listHeight < 8) listHeight = 8;
						if (listHeight) dropdownCSS.height = Math.ceil(listHeight) * 22;

						level1.subPages.forEach((level2) => {
							const level2URI = prefix(setup, removeLanguagePrefix(translate(setup, level2, 'uri')));
							const level2Styles = level2.styles || null;

							if (level2.subPages) {
								level2SubCategoryLinks.push(
									<div key={urlify(level2URI)} className={[s.level2_block, ...(level2.subPages.length > 9 ? [s.level2_block__doubleColumn] : [])].join(' ')} style={dropdownCSS}>
										<div className={s.level2_block_item}>
											<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level2Link'].join(' ')} to={level2URI} style={level2Styles}>
												<span className={[s.level2_block_item_link_underlined, 'a-level2Link'].join(' ')}>{translate(setup, level2, 'title')}</span>
											</Link>
										</div>
										{
											level2.subPages.map((level3) => {
												const level3URI = prefix(setup, translate(setup, level3, 'uri'));
												const level3Styles = level3.styles || null;

												return (
													<div className={s.level2_block_item} key={urlify(level3URI)}>
														<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level3Link'].join(' ')} to={level3URI} style={level3Styles}>
															<span className="a-level3Link">{translate(setup, level3, 'title')}</span>
														</Link>
													</div>
												);
											})
										}
									</div>,
								);
							} else {
								level2Links.push(
									<div className={s.level2_block_item} key={urlify(level2URI)}>
										<Link onClick={resetFilterHandler} className={[s.level2_block_item_link, 'a-level2Link'].join(' ')} to={level2URI}>
											<span className="a-level2Link">{translate(setup, level2, 'title')}</span>
										</Link>
									</div>,
								);
							}
						});
					}

					const level1Click = {};
					let expandable = null;
					if (level1.subPages) {
						level1Click.onClick = expandMenuHandler;
						expandable = <span className={s.level1_column_link_expandIndicator} />;
					} else {
						level1Click.onClick = resetFilterHandler;
					}

					const hasCampaigns = {};
					const { localized = {}, filterArrays = {} } = productCache?.[setup.market]?.[setup.pricelist] || {};
					const { newProducts = false, productsOnSale = false, categories: rawCategories = {} } = filterArrays;
					const isLocalized = localized[setup.language];
					const categories = isLocalized ? Object.keys(rawCategories).reduce((acc, category) => {
						const categoryKey = isLocalized.categories[category] ? isLocalized.categories[category].key : category;
						return ({ ...acc, [categoryKey]: [...rawCategories[category]] });
					}, {}) : rawCategories;

					if ((newProducts || productsOnSale) && categories[level1URI]) {
						const categoryItems = categories.archive ? difference(categories[level1URI], categories.archive) : categories[level1URI];
						if (newProducts && intersect(categoryItems, newProducts).length > 0) hasCampaigns.newProducts = 1;
						if (productsOnSale && intersect(categoryItems, productsOnSale).length > 0) hasCampaigns.productsOnSale = 1;
					}

					return (
						<li className={[s.level1_column, ...(navigation.expandedMenuItem === i ? [s.level1_column__expanded] : [])].join(' ')} key={urlify(combinedURILevel1)} style={level1Styles}>
							<Link className={[s.level1_column_link, (combinedURILevel1.indexOf('sale') !== -1 || combinedURILevel1.indexOf('rea') !== -1 ? s.level1_column_link__sale : null)].join(' ')} to={combinedURILevel1} {...level1Click}>{expandable}<span className="a-level1Link">{translate(setup, level1, 'title')}</span></Link>
							{
								level1.subPages && (
									<div className={s.level1_columnInner}>
										<div className={[s.level2, (level2Links.length > 0 ? s.level2__minimal : null)].join(' ')}>
											{
												((level1.campaigns && Object.keys(hasCampaigns).length > 0) || level2Links.length > 0) && (
													<div className={s.level2_block__row}>
														{ level2Links.length > 0 && level2Links }
													</div>
												)
											}
											{ level2SubCategoryLinks }
										</div>
									</div>
								)
							}
						</li>
					);
				})
			}
			<li className={s.navigation_settings}>
				<div className={s.navigation_settings_languages}>
					{
						Object.keys(setup.languages).map((lang) => (
							<div key={lang} className={[s.navigation_settings_language, ...(lang === setup.language ? [s.navigation_settings_language__selected] : [])].join(' ')}>
								<Link to={`/${lang}/${pagePaths && pagePaths[lang] ? pagePaths[lang] : ''}`} onClick={changeLanguage} data-language={lang}>{ setup.languages[lang].name }</Link><span />
							</div>
						))
					}
				</div>
				<div className={s.navigation_settings_country}>
					<Select id="header-country" selected={setup.country} options={countries} inputChange={onCountryChange} />
				</div>
			</li>
		</ul>
	</nav>
);

export default hot(module)(Navigation);
